// Business Detail Component
// Feature: 005-business-administration

import { useTranslation } from 'react-i18next';
import {
  MapPin,
  Calendar,
  User,
  Building2,
} from 'lucide-react';
import { Edit } from 'lucide-react';
import type { BusinessWithOwner, CategoryOption, BusinessProfileUpdate } from '@/types/businessAdmin';
import { BusinessStatusBadge } from './BusinessStatusBadge';
import { BusinessPhotoGallery } from './BusinessPhotoGallery';
import { BusinessActions } from './BusinessActions';
import { BusinessEditForm } from './BusinessEditForm';

interface BusinessDetailProps {
  business: BusinessWithOwner;
  categories: CategoryOption[];
  isEditing: boolean;
  onApprove: () => Promise<{ success: boolean; error?: string }>;
  onDisable: (reason: string) => Promise<{ success: boolean; error?: string }>;
  onReactivate: () => Promise<{ success: boolean; error?: string }>;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: (updates: BusinessProfileUpdate, newPhotos: File[]) => Promise<{ success: boolean; errors?: Record<string, string> }>;
}

export function BusinessDetail({
  business,
  categories,
  isEditing,
  onApprove,
  onDisable,
  onReactivate,
  onEdit,
  onCancelEdit,
  onSaveEdit,
}: BusinessDetailProps) {
  const { t, i18n } = useTranslation('businessAdmin');

  // If in edit mode, show edit form
  if (isEditing) {
    return (
      <BusinessEditForm
        business={business}
        categories={categories}
        onSave={onSaveEdit}
        onCancel={onCancelEdit}
      />
    );
  }

  // Normalize language to base code (en-GB -> en, km-KH -> km)
  const getBaseLanguage = () => {
    const lang = i18n.language.split('-')[0];
    return (lang === 'km' ? 'km' : 'en') as 'en' | 'km';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const baseLang = getBaseLanguage();
    // Use a safe locale for DateTimeFormat
    const locale = baseLang === 'km' ? 'km-KH' : 'en-US';
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between pb-4 border-b border-neutral-200">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">
            {business.data.name}
          </h2>
          <BusinessStatusBadge status={business.data.status} />
        </div>
        <div className="flex gap-2">
          <BusinessActions
            currentStatus={business.data.status}
            onApprove={onApprove}
            onDisable={onDisable}
            onReactivate={onReactivate}
          />
          <button
            onClick={onEdit}
            className="flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50 transition"
          >
            <Edit size={18} />
            {t('actions.edit')}
          </button>
        </div>
      </div>

      {/* Description */}
      <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-5">
        <h3 className="text-lg font-semibold text-neutral-900 mb-3">
          Description
        </h3>
        <p className="text-neutral-700 leading-relaxed">
          {business.data.description}
        </p>
      </div>

      {/* Business Info Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Category & Tags Card */}
        <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-5">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
            <Building2 size={20} className="text-primary-600" />
            Category & Tags
          </h3>
          <div className="space-y-4">
            <div>
              <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">Category</p>
              <p className="text-sm font-medium text-neutral-900">{business.data.categoryName || 'Uncategorized'}</p>
            </div>
            {business.data.tags && business.data.tags.length > 0 && (
              <div>
                <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-2">Tags</p>
                <div className="flex flex-wrap gap-2">
                  {business.data.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="text-xs px-2.5 py-1 bg-primary-100 text-primary-700 rounded-full font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Location Card */}
        <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-5">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
            <MapPin size={20} className="text-primary-600" />
            Location
          </h3>
          <div className="space-y-3">
            {business.data.city && (
              <div>
                <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">City</p>
                <p className="text-sm font-medium text-neutral-900">{business.data.city}</p>
              </div>
            )}
            <div>
              <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">Coordinates</p>
              <p className="text-xs font-mono text-neutral-600">
                {business.data.location?.lat != null && business.data.location?.lng != null 
                  ? `${business.data.location.lat.toFixed(6)}, ${business.data.location.lng.toFixed(6)}`
                  : 'Not set'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Owner Information Card */}
      <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-5">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
          <User size={20} className="text-neutral-600" />
          Business Owner
        </h3>
        <div className="text-sm text-neutral-500 italic">
          No owner assigned. Business is available to claim.
        </div>
      </div>

      {/* Photos */}
      <div>
        <h3 className="text-sm font-semibold text-neutral-700 mb-3">
          {t('details.photos')}
        </h3>
        <BusinessPhotoGallery
          primaryPhoto={business.data.photos?.[0] || business.data.photo_url}
          additionalPhotos={business.data.photos?.slice(1) || []}
          businessName={business.data.name}
        />
      </div>

      {/* Metadata */}
      <div className="border-t border-neutral-200 pt-4">
        <h3 className="text-sm font-semibold text-neutral-700 mb-3">
          {t('details.metadata')}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-neutral-400" />
            <span className="text-neutral-600">Created:</span>
            <span className="text-neutral-900">{formatDate(business.created_at)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-neutral-400" />
            <span className="text-neutral-600">Updated:</span>
            <span className="text-neutral-900">{formatDate(business.updated_at)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
